import type { ReactNode } from "react";
import { Badge } from "../../../ui/common-ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/common-ui/card";
import { AlertTriangle, Boxes, CheckCircle2, Layers3, Server, Shield, Zap } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { GraphNode } from "../types/graph";
import { ResourceGroupList, type GroupedResource } from "./ResourceGroupList";

interface BlastRadiusInspectorProps {
  node: GraphNode;
}

const SEVERITY_STYLES: Record<string, string> = {
  CRITICAL: "bg-red-500/20 text-red-500 border-red-500/30",
  HIGH: "bg-orange-500/20 text-orange-500 border-orange-500/30",
  MEDIUM: "bg-yellow-500/20 text-yellow-500 border-yellow-500/30",
  LOW: "bg-blue-500/20 text-blue-500 border-blue-500/30",
};

const HIDDEN_PROPS = new Set([
  "isBlastRadiusMode",
  "isBlastRadiusNode",
  "isBlastRadiusCircular",
  "blastRadiusNodeKind",
  "blastRadiusInfo",
  "blastRadiusId",
  "graphId",
  "severity",
  "riskSeverity",
  "riskScore",
  "reason",
  "resources",
  "impactKey",
  "summary",
  "roleName",
  "resourceKind",
  "mostUsed",
]);

const severityStyle = (severity?: string) =>
  SEVERITY_STYLES[String(severity || "").toUpperCase()] || SEVERITY_STYLES.MEDIUM;

const humanize = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());

function SectionCard({
  title,
  icon: Icon,
  iconWrapperClass,
  iconClass,
  children,
}: {
  title: string;
  icon: LucideIcon;
  iconWrapperClass: string;
  iconClass: string;
  children: ReactNode;
}) {
  return (
    <Card className="border-border/50 bg-card shadow-lg">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${iconWrapperClass}`}>
            <Icon className={`w-4 h-4 ${iconClass}`} />
          </div>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

function DetailRows({ props }: { props: Record<string, any> }) {
  const entries = Object.entries(props).filter(
    ([key, value]) =>
      !HIDDEN_PROPS.has(key) &&
      value !== null &&
      value !== undefined &&
      value !== "" &&
      typeof value !== "object",
  );

  if (entries.length === 0) return null;

  return (
    <div className="space-y-2">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-start justify-between gap-3 text-xs">
          <span className="text-muted-foreground">{humanize(key)}</span>
          <span className="max-w-[60%] break-all text-right font-mono text-foreground">{String(value)}</span>
        </div>
      ))}
    </div>
  );
}

export function BlastRadiusInspector({ node }: BlastRadiusInspectorProps) {
  const info = node.props?.blastRadiusInfo;
  const props = node.props || {};
  const kind = String(props.blastRadiusNodeKind || "IDENTITY");

  if (!info) {
    return (
      <Card className="border-border/50 bg-card shadow-lg">
        <CardContent className="p-6 text-center text-muted-foreground">
          <Zap className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-xs">No blast radius details available</p>
        </CardContent>
      </Card>
    );
  }

  const severity = String(props.riskSeverity || info.risk?.severity || "HIGH").toUpperCase();

  if (kind === "ROLE") {
    return (
      <div className="space-y-4">
        <SectionCard title="Assigned Role" icon={Shield} iconWrapperClass="bg-cyan-500/10" iconClass="text-cyan-500">
          <div>
            <p className="text-sm font-semibold text-foreground">{node.label}</p>
            <p className="mt-1 break-all font-mono text-[10px] text-muted-foreground">{String(props.roleId || "")}</p>
          </div>
          {props.summary && <p className="text-xs leading-relaxed text-foreground">{String(props.summary)}</p>}
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={`text-[10px] font-semibold ${severityStyle(severity)}`}>
              {severity}
            </Badge>
            {typeof props.riskScore === "number" && (
              <Badge variant="secondary" className="text-[10px]">
                Risk {props.riskScore}/100
              </Badge>
            )}
            {props.custom ? <Badge variant="outline" className="text-[10px]">Custom role</Badge> : null}
            {props.conditional ? (
              <Badge variant="outline" className="border-yellow-500/30 text-[10px] text-yellow-600">
                Conditional
              </Badge>
            ) : null}
            {props.resolved === false ? (
              <Badge variant="outline" className="text-[10px]">Definition not synced</Badge>
            ) : null}
          </div>
        </SectionCard>

        <SectionCard title="Role Details" icon={Layers3} iconWrapperClass="bg-violet-500/10" iconClass="text-violet-500">
          <DetailRows props={props} />
        </SectionCard>
      </div>
    );
  }

  if (kind === "IMPACT" || kind === "ACCESS") {
    return (
      <div className="space-y-4">
        <SectionCard title="What This Grants" icon={Layers3} iconWrapperClass="bg-orange-500/10" iconClass="text-orange-500">
          <div>
            <p className="text-sm font-semibold text-foreground">{node.label}</p>
            {props.reason && <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{String(props.reason)}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={`text-[10px] font-semibold ${severityStyle(severity)}`}>
              {severity}
            </Badge>
            {props.accessLevel && <Badge variant="outline" className="text-[10px]">{String(props.accessLevel)}</Badge>}
            {props.resourceScope === "ALL" && typeof props.resourceCount === "number" && props.resourceCount > 0 && (
              <Badge variant="secondary" className="text-[10px]">All {props.resourceCount} in scope</Badge>
            )}
            {props.resourceType && (
              <Badge variant="outline" className="text-[10px]">{humanize(String(props.resourceType))}</Badge>
            )}
          </div>
        </SectionCard>
      </div>
    );
  }

  if (kind === "RESOURCE_GROUP") {
    const resources = (props.resources || []) as GroupedResource[];
    return (
      <div className="space-y-4">
        <SectionCard title="Resources In Scope" icon={Boxes} iconWrapperClass="bg-teal-500/10" iconClass="text-teal-500">
          <div>
            <p className="text-sm font-semibold text-foreground">{node.label}</p>
            {props.reason && <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{String(props.reason)}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={`text-[10px] font-semibold ${severityStyle(severity)}`}>
              {severity}
            </Badge>
            {props.accessLevel && <Badge variant="outline" className="text-[10px]">{String(props.accessLevel)}</Badge>}
            {typeof props.resourceCount === "number" && (
              <Badge variant="secondary" className="text-[10px]">{props.resourceCount} total</Badge>
            )}
          </div>
          {resources.length > 0 ? (
            <ResourceGroupList resources={resources} />
          ) : (
            <p className="text-xs text-muted-foreground">No resource detail was captured for this group.</p>
          )}
        </SectionCard>
      </div>
    );
  }

  if (kind === "RESOURCE") {
    const activity = Number(props.activityCount || 0);
    return (
      <div className="space-y-4">
        <SectionCard title="Reachable Resource" icon={Server} iconWrapperClass="bg-amber-500/10" iconClass="text-amber-500">
          <div>
            <p className="text-sm font-semibold text-foreground">{node.label}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {activity > 0
                ? `${activity} recorded actions in the analysed window.`
                : "No recorded activity in the analysed window."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {props.resourceType && (
              <Badge variant="outline" className="text-[10px]">{humanize(String(props.resourceType))}</Badge>
            )}
            {props.accessLevel && <Badge variant="outline" className="text-[10px]">{String(props.accessLevel)}</Badge>}
          </div>
        </SectionCard>

        <SectionCard title="Resource Details" icon={Layers3} iconWrapperClass="bg-violet-500/10" iconClass="text-violet-500">
          <DetailRows props={props} />
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SectionCard title="Blast Radius Summary" icon={Zap} iconWrapperClass="bg-red-500/10" iconClass="text-red-500">
        <p className="text-xs leading-relaxed text-foreground">{info.summary}</p>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className={`text-[10px] font-semibold ${severityStyle(severity)}`}>
            {severity}
          </Badge>
          {typeof info.risk?.score === "number" && (
            <Badge variant="secondary" className="text-[10px]">Risk {info.risk.score}/100</Badge>
          )}
        </div>
      </SectionCard>

      {info.whyThisMatters && (
        <SectionCard title="Why This Matters" icon={AlertTriangle} iconWrapperClass="bg-orange-500/10" iconClass="text-orange-500">
          <p className="text-xs leading-relaxed text-foreground">{info.whyThisMatters}</p>
        </SectionCard>
      )}

      {info.risk?.reason && (
        <Card className="border-border/50 bg-card shadow-lg border-l-4 border-l-red-500/50">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-red-500 mb-1">Risk</p>
            <p className="text-xs leading-relaxed text-foreground">{info.risk.reason}</p>
          </CardContent>
        </Card>
      )}

      {info.recommendedActions?.length > 0 && (
        <SectionCard title="Recommended Actions" icon={CheckCircle2} iconWrapperClass="bg-green-500/10" iconClass="text-green-500">
          {info.recommendedActions.map((action: string, index: number) => (
            <div key={index} className="flex items-start gap-2 rounded-lg border border-green-500/20 bg-green-500/5 p-2">
              <CheckCircle2 className="mt-0.5 w-3.5 h-3.5 shrink-0 text-green-500" />
              <span className="text-xs text-foreground">{action}</span>
            </div>
          ))}
        </SectionCard>
      )}
    </div>
  );
}
